import { z } from 'zod'
import {
  RegionSchema,
  DMSSchema,
  TokenSchema,
  UserSchema,
  OrganizationSchema,
  DealUsershipSchema,
  DMSDocumentSchema,
  DealSchemaForDMS,
  DealStatusSchema,
  ServiceSchema,
  CurrentUserSchema,
  ClosingBookSchema,
  NewClosingBookDataSchema,
  ClosingBookDocumentSetSchema,
  ClosingBookEventSchema,
  ClosingBookTemplateSchema,
  ClosingBookPreviewJobsSchema,
  RoleSchema,
  DealroomUserSchema,
  AgendaObjectCBSchema,
  AgendaObjectServerSchema,
  AgendaColumnSchema,
  ItemSchema,
  ActionItemSchema,
  ActionItemStatusSchema,
  CategorySchema,
  RedactedItemSchema,
  RedactedActionItemSchema,
  RedactedCategorySchema,
  UploadSchema,
  FileMatchSchema,
  FirmSchema,
  AgendaSchema,
  RegistrationSchema,
  DealInviteSchema,
  DealSchema,
  UploadRawUrlSchema,
  ClosingVersionSchema,
  SnapshotSchema,
  RedactedSnapshotSchema,
  MarkupSchema,
  BlacklineSchema,
  VersionSchema,
  CreateVersionPayloadSchema,
  VersionCellDataSchema,
  OrganizationRegistrationSchema,
  AccessCellDataSchema,
  AuditTokenSchema,
  AllTrashSchema,
  TrashedUploadSchema,
  TrashedVersionSchema,
  TrashedItemSchema,
  TrashedActionItemSchema,
  TrashedCategorySchema,
  TrashedMarkupSchema,
  TrashedFirmSchema,
  BulkUntrashResponseSchema,
  BulkUntrashPayloadSchema,
  VersionMinimalSchema,
  ResponsibleLawyerSchema,
  EmbeddedUserSchema,
  TitleCellDataSchema,
  ResponsibilityCellDataSchema,
  ActionItemCellDataSchema,
  SignatureCellDataSchema,
  ScheduleCellDataSchema,
  NoteCellDataSchema,
  TextCellDataSchema,
  NumberCellDataSchema,
  DateCellDataSchema,
  DealVariableSchema,
  ItemWithVariableSchema,
  ItemVariableSchema,
  AgendaNoteSchema,
  RenderedVersionSchema,
  VersionRenderedVariableSchema,
  VersionDetectedVariableSchema,
  ScheduleSchema,
  SignatureSchema,
  SignaturePageSchema,
  SignaturePageTemplateSchema,
  AddSignaturesRequestSchema,
  AddSignaturesResponseSchema,
  UpdateSignatureCoordinatesSchema,
  EntitySchema,
  IndividualSchema,
  SigningPositionSchema,
  AgendaColumnEntrySchema,
  CustomEntryCellDataSchema,
  SelectOptionSchema,
  DealFieldSchema,
  TextConfigSchema,
  NumberConfigSchema,
  SingleSelectConfigSchema,
  CustomDealFieldValueSchema,
  CustomDealFieldSetSchema,
  ExportRequestSchema,
  SendDocumentsRequestSchema,
  SendDocumentsAttachmentSchema,
  AttachmentTypeSchema,
  AttachmentFormatSchema,
  ItemApprovalSchema,
  ApprovalGroupSchema,
  ApproverSchema,
  RedactedItemApprovalSchema,
  RedactedApproverSchema,
  BulkCreateItemApprovalsRequestSchema,
  ManageApproversRequestSchema,
  SendApprovalRequestsRequestSchema,
  EditApprovalMessageRequestSchema,
  RejectApproverRequestSchema,
  ApprovalsBatchPayloadSchema,
  DealCreateSchema,
  DealInviteDomainSchema,
  SaveAsTemplatePayloadSchema,
  DealTemplateSchema,
} from './schemas'
import { DealListItemWithUsership } from './api/deals'

export type InferApiResponse<AsyncFn> = AsyncFn extends (
  ...args: infer _
) => Promise<infer ResponseType>
  ? ResponseType
  : never

export enum UserState {
  ACTIVE = 'active',
  DEACTIVATED = 'deactivated',
  DELETED = 'deleted',
  INVITED = 'invited', // Only received from the user stats endpoint, these are email registrations, but are not users yet
}

export type Deal = z.infer<typeof DealSchema>
export type DealCreate = z.infer<typeof DealCreateSchema>
export type SaveAsTemplatePayload = z.infer<typeof SaveAsTemplatePayloadSchema>
export type DealTemplate = z.infer<typeof DealTemplateSchema>
export type DealType = z.infer<typeof DealSchemaForDMS>
export type DealStatus = z.infer<typeof DealStatusSchema>
export type Region = z.infer<typeof RegionSchema>
export type DMS = z.infer<typeof DMSSchema>
export type DMSDocument = z.infer<typeof DMSDocumentSchema>
export type Service = z.infer<typeof ServiceSchema>

export type AuthToken = z.infer<typeof TokenSchema>
export type AuthUserData = z.infer<typeof UserSchema>
export type AuthCurrentUser = z.infer<typeof CurrentUserSchema>
export type AuthOrganization = z.infer<typeof OrganizationSchema>
export type Registration = z.infer<typeof RegistrationSchema>
export type OrganizationRegistration = z.infer<typeof OrganizationRegistrationSchema>

export type DealUsership = z.infer<typeof DealUsershipSchema>
export type DealInvite = z.infer<typeof DealInviteSchema>
export type DealInviteDomain = z.infer<typeof DealInviteDomainSchema>
export type MergedDealProps = DealListItemWithUsership
export type DealroomUserData = z.infer<typeof DealroomUserSchema>
export type User = z.infer<typeof UserSchema>
export type EmbeddedUser = z.infer<typeof EmbeddedUserSchema>
export type ResponsibleLawyer = z.infer<typeof ResponsibleLawyerSchema>
export type Firm = z.infer<typeof FirmSchema>

export type FeatureFlagName =
  | 'add_multiple_users'
  | 'maintenance_banner'
  | 'hide_maintenance_banner'
  | 'imanage10'
  | 'imanage_10_enabled'
  | 'beamer'
  | 'adobe_sign'
  | 'hide_docusign'
  | 'onespan'
  | 'bada_audit'
  | 'minimal_versions'
  | 'llm_detection_button'
  | 'llm_agenda_importer'
  | 'llm_agenda_import_deal_specific'
  | 'hide_feature_opt_in_banner'
  | 'llm_opt_out'
  | 'llm_agenda_import_opt_out'
  | 'react_dealroom_overview'
  | 'react_ai_importer'
  | 'react_agenda_importer'
  | 'react_approvals_wizard'
  | 'system_deal_fields'
  | 'work_drag_and_drop'
  | 'disable_support_sending'
  | 'disable_support_agenda_imports'
  | 'maintenance_banner_au' // Evergreen
  | 'maintenance_banner_eu' // Evergreen
  | 'maintenance_banner_na' // Evergreen
  | 'project_muffin'
  | 'invite_allowlist' // Evergreen
  | 'castle_deal_list'

export type ClosingBook = z.infer<typeof ClosingBookSchema>
export type NewClosingBookData = z.infer<typeof NewClosingBookDataSchema>
export type ClosingBookDocumentSet = z.infer<typeof ClosingBookDocumentSetSchema>
export type ClosingBookEvent = z.infer<typeof ClosingBookEventSchema>
export type ClosingBookTemplate = z.infer<typeof ClosingBookTemplateSchema>
export type ClosingBookPreviewJobs = z.infer<typeof ClosingBookPreviewJobsSchema>

export type Role = z.infer<typeof RoleSchema>

export type Upload = z.infer<typeof UploadSchema>
export type UploadRawUrl = z.infer<typeof UploadRawUrlSchema>
export type FileMatch = z.infer<typeof FileMatchSchema>

export type AgendaObject = z.infer<typeof AgendaObjectCBSchema>
export type AgendaObjectServer = z.infer<typeof AgendaObjectServerSchema>
export type AgendaColumn = z.infer<typeof AgendaColumnSchema>
export type AgendaColumnEntry = z.infer<typeof AgendaColumnEntrySchema>
export type AgendaType = z.infer<typeof AgendaSchema>
export type Item = z.infer<typeof ItemSchema>
export type ActionItem = z.infer<typeof ActionItemSchema>
export type ActionItemStatus = z.infer<typeof ActionItemStatusSchema>
export type Category = z.infer<typeof CategorySchema>
export type RedactedItem = z.infer<typeof RedactedItemSchema>
export type RedactedActionItem = z.infer<typeof RedactedActionItemSchema>
export type RedactedCategory = z.infer<typeof RedactedCategorySchema>
export type ClosingVersion = z.infer<typeof ClosingVersionSchema>
export type Snapshot = z.infer<typeof SnapshotSchema>
export type RedactedSnapshot = z.infer<typeof RedactedSnapshotSchema>
export type Markup = z.infer<typeof MarkupSchema>
export type Blackline = z.infer<typeof BlacklineSchema>
export type ExportRequest = z.infer<typeof ExportRequestSchema>
export type SendDocumentsRequest = z.infer<typeof SendDocumentsRequestSchema>
export type SendDocumentsAttachment = z.infer<typeof SendDocumentsAttachmentSchema>
export type AttachmentType = z.infer<typeof AttachmentTypeSchema>
export type AttachmentFormat = z.infer<typeof AttachmentFormatSchema>
export type Version = z.infer<typeof VersionSchema>
export type VersionMinimal = z.infer<typeof VersionMinimalSchema>
export type VersionCellData = z.infer<typeof VersionCellDataSchema>
export type AccessCellData = z.infer<typeof AccessCellDataSchema>
export type TitleCellData = z.infer<typeof TitleCellDataSchema>
export type ActionItemCellData = z.infer<typeof ActionItemCellDataSchema>
export type NoteCellData = z.infer<typeof NoteCellDataSchema>
export type SignatureCellData = z.infer<typeof SignatureCellDataSchema>
export type ScheduleCellData = z.infer<typeof ScheduleCellDataSchema>
export type ResponsibilityCellData = z.infer<typeof ResponsibilityCellDataSchema>
export type DateCellData = z.infer<typeof DateCellDataSchema>
export type CustomEntryCellData = z.infer<typeof CustomEntryCellDataSchema>
export type TextCellData = z.infer<typeof TextCellDataSchema>
export type NumberCellData = z.infer<typeof NumberCellDataSchema>
export type DealVariableType = z.infer<typeof DealVariableSchema>
export type ItemVariable = z.infer<typeof ItemVariableSchema>
export type ItemWithVariable = z.infer<typeof ItemWithVariableSchema>
export type AgendaNote = z.infer<typeof AgendaNoteSchema>
export type Schedule = z.infer<typeof ScheduleSchema>
export type Signature = z.infer<typeof SignatureSchema>
export type SignaturePage = z.infer<typeof SignaturePageSchema>
export type SignaturePageTemplate = z.infer<typeof SignaturePageTemplateSchema>
export type AddSignaturesRequest = z.infer<typeof AddSignaturesRequestSchema>
export type AddSignaturesResponse = z.infer<typeof AddSignaturesResponseSchema>
export type UpdateSignatureCoordinates = z.infer<typeof UpdateSignatureCoordinatesSchema>
export type Entity = z.infer<typeof EntitySchema>
export type Individual = z.infer<typeof IndividualSchema>
export type SigningPosition = z.infer<typeof SigningPositionSchema>
export type RenderedVersion = z.infer<typeof RenderedVersionSchema>
export type VersionRenderedVariable = z.infer<typeof VersionRenderedVariableSchema>
export type VersionDetectedVariable = z.infer<typeof VersionDetectedVariableSchema>

export type AuditToken = z.infer<typeof AuditTokenSchema>

export type AllTrash = z.infer<typeof AllTrashSchema>
export type TrashedUpload = z.infer<typeof TrashedUploadSchema>
export type TrashedVersion = z.infer<typeof TrashedVersionSchema>
export type TrashedItem = z.infer<typeof TrashedItemSchema>
export type TrashedActionItem = z.infer<typeof TrashedActionItemSchema>
export type TrashedCategory = z.infer<typeof TrashedCategorySchema>
export type TrashedMarkup = z.infer<typeof TrashedMarkupSchema>
export type TrashedFirm = z.infer<typeof TrashedFirmSchema>
export type BulkUntrashPayload = z.infer<typeof BulkUntrashPayloadSchema>
export type BulkUntrashResponse = z.infer<typeof BulkUntrashResponseSchema>
export type BulkEmptyTrashPayload = BulkUntrashPayload
export type BulkTrashPayload = BulkUntrashPayload
export type BulkMarkFinalPayload = {
  version_ids: number[]
}
export type BulkCompilePageRange = {
  document_id: number
  document_type: string
  start_page: number
  end_page: number | null
}

export type BulkCompilePageRangeResponse = {
  document_id: number
  document_type: number
  start_page: number
  end_page: number | null
}

export type BulkCompileItem = {
  item_id: number
  page_ranges: BulkCompilePageRange[]
}

export type BulkCompileItemResponse = {
  item_id: number
  page_ranges: BulkCompilePageRangeResponse[]
}

export type BulkCompileClosingVersionPayload = {
  items: BulkCompileItem[]
}

export type ItemPayload = Item & { responsible_firm_ids?: number[] }
export type ActionItemPayload = ActionItem & { responsible_firm_ids?: number[] }
export type CreateVersionPayload = z.infer<typeof CreateVersionPayloadSchema>
export type CreateNotePayload = {
  agenda_note: {
    body: string
    content_type: 'item' | 'action-item'
    deal_id: number
    created_by_id: number
    item_id: number | null
    action_item_id: number | null
    created_by_name: null
    date_created: null
    is_resolved: boolean
    last_updated: null
  }
}

export type CreateSnapshotPayload = {
  snapshot: {
    deal_id: number
    label: string
    create_content_as_group_id?: number
    is_failed: boolean
    gas_group_ids: number[]
    gas_is_public: boolean
    orientation: 'landscape' | 'portrait'
  }
}

export type CreateBlacklinePayload = {
  deal_id: number
  content_type: Blackline['content_type']
  old_id: number
  new_id: number
}

export type UpdatePositionsPayload = {
  agenda_object_ids: number[]
  pre_agenda_object_id: null | number
  post_agenda_object_id: null | number
}

export type CopyAgendaObjectsPayload = UpdatePositionsPayload

export type RerankAgendaObjectsPayload = {
  action: 'promote' | 'demote'
  agenda_object_ids: number[]
}

export type UpdateNumberingPayload = {
  always_nest_numbers: boolean
  number_styles: string[]
  number_resets: (number | null)[]
}

export type SetAgendaFirmsPayload = {
  firm_ids: number[]
  item_ids: number[]
  action_item_ids: number[]
  replace: boolean
}

export type SetItemsCtypePayload = {
  item_ids: number[]
  ctype: 'counterpart' | 'original'
}

export type ContentTypes = 'item' | 'action-item' | 'category'
export type AgendaObjectConversionPayload = {
  item_ids: number[]
  action_item_ids: number[]
  category_ids: number[]
  convert_type: ContentTypes
}

// Bulk Add Payloads
export type SerializedAgendaObject = {
  content_type: ContentTypes
  number_level: number
  content_payload: {
    title: string
    note: string | null
    responsible_firm_ids: number[]
    gas_group_ids: number[]
    gas_is_public: boolean
    custom_columns: {
      id: number
      value: string
    }[]
  }
}
export type BulkAddAgendaObjectsPayload = {
  agenda_objects: SerializedAgendaObject[]
  batched: number
  post_agenda_object_id: number | null
  pre_agenda_object_id: number | null
}

export type DeleteAgendaColumnsPayload = {
  deleted_agenda_columns: number[]
}

export type CreateAgendaColumnsPayload = {
  agenda_columns: {
    column_type: 'text' | 'date' | 'number'
    column_title: string
  }[]
}

export type CompleteAgendaImportPayload = {
  headers: {
    id: string
    label: string
    type: string
  }[]
}

export type ModifyAgendaColumnsPayload = {
  agenda_columns: {
    id?: number
    column_title: string
    column_type: AgendaColumn['column_type']
    error: string
    is_visible: boolean
    options?: {
      id?: number
      label: string
      color: number
      status_category?: 'open' | 'in-progress' | 'closed' | null
    }[]
  }[]
}

export type SelectOption = z.infer<typeof SelectOptionSchema>
export type DealField = z.infer<typeof DealFieldSchema>
export type DealFieldType = DealField['deal_field_type']
export type DealFieldState = DealField['state']
export type TextConfig = z.infer<typeof TextConfigSchema>
export type NumberConfig = z.infer<typeof NumberConfigSchema>
export type SingleSelectConfig = z.infer<typeof SingleSelectConfigSchema>
export type CustomDealFieldValue = z.infer<typeof CustomDealFieldValueSchema>
export type CustomDealFieldSet = z.infer<typeof CustomDealFieldSetSchema>

export type ItemApproval = z.infer<typeof ItemApprovalSchema>
export type ApprovalGroup = z.infer<typeof ApprovalGroupSchema>
export type Approver = z.infer<typeof ApproverSchema>
export type RedactedItemApproval = z.infer<typeof RedactedItemApprovalSchema>
export type RedactedApprover = z.infer<typeof RedactedApproverSchema>
export type BulkCreateItemApprovalsRequest = z.infer<typeof BulkCreateItemApprovalsRequestSchema>
export type ManageApproversRequest = z.infer<typeof ManageApproversRequestSchema>
export type SendApprovalRequestsRequest = z.infer<typeof SendApprovalRequestsRequestSchema>
export type EditApprovalMessageRequest = z.infer<typeof EditApprovalMessageRequestSchema>
export type RejectApproverRequest = z.infer<typeof RejectApproverRequestSchema>
export type ApprovalsBatchPayload = z.infer<typeof ApprovalsBatchPayloadSchema>
